/**
 * 预算相关计算（与配菜组合算法无关）
 *
 * 总预算 = 人数 × 人均餐标
 * 总售价不得超过总预算
 * 预估利润率 = (总售价 − 总成本) / 总成本
 */

const EPS = 1e-8

/**
 * @param {number} guestCount 顾客人数
 * @param {number} perCapita 人均餐标
 */
export function calcBudget(guestCount, perCapita) {
  const totalBudget = Number(guestCount) * Number(perCapita)
  return {
    totalBudget,
    maxPrice: totalBudget
  }
}

/**
 * 预估利润率（相对总成本，与录入目标利润率同一口径）
 * (总售价 − 总成本) / 总成本
 * 总成本为 0 时返回 null，页面展示 "-"
 */
export function calcEstimatedProfitRate(totalPrice, totalCost) {
  if (!totalCost) return null
  return (totalPrice - totalCost) / totalCost
}

export function isPriceOverBudget(totalPrice, totalBudget) {
  if (!totalBudget) return false
  return Number(totalPrice) > Number(totalBudget) + EPS
}

/** 目标利润率有填写且大于 0 时下限为目标，否则默认不低于 50%；无上限 */
export function hasTargetProfitRate(raw) {
  return raw !== '' && raw != null && Number.isFinite(Number(raw)) && Number(raw) > 0
}

export const DEFAULT_PROFIT_MIN = 0.5

export function getProfitRateFloor(profitRatePercent) {
  if (hasTargetProfitRate(profitRatePercent)) {
    return Number(profitRatePercent) / 100
  }
  return DEFAULT_PROFIT_MIN
}

/** 预估利润率低于允许下限时返回说明，否则返回 null */
export function estimatedRateIssue(estimatedRate, profitRatePercent) {
  const min = getProfitRateFloor(profitRatePercent)
  const hasTarget = hasTargetProfitRate(profitRatePercent)
  if (estimatedRate == null || estimatedRate + EPS < min) {
    return hasTarget
      ? '预估利润率不得低于目标利润率'
      : '未填写目标利润率时，预估利润率不得低于 50%'
  }
  return null
}

/**
 * 单人份 × 人数，得到菜单行上的菜品售价/成本
 */
export function toMenuLine(dish, guestCount) {
  const n = Number(guestCount) || 1
  return {
    ...dish,
    qty: n,
    linePrice: Number(dish.price) * n,
    lineCost: Number(dish.cost) * n
  }
}

/**
 * 用指定菜品列表生成配菜结果（不跑自动算法），售价/成本按人数折算
 */
export function buildMenuResult(sourceDishes, input) {
  const guestCount = Number(input.guestCount)
  const n = Number.isInteger(guestCount) && guestCount >= 1 ? guestCount : 1
  const perCapita = Number(input.perCapita)
  const hasCapita = Number.isFinite(perCapita) && perCapita > 0
  const budget = hasCapita
    ? calcBudget(n, perCapita)
    : { totalBudget: 0, maxPrice: 0 }
  const dishes = (sourceDishes || []).map((d) => toMenuLine(d, n))
  const totalPrice = dishes.reduce((sum, d) => sum + Number(d.linePrice), 0)
  const totalCost = dishes.reduce((sum, d) => sum + Number(d.lineCost), 0)
  return {
    ok: true,
    algorithm: 'manual',
    ...budget,
    dishes,
    totalPrice,
    totalCost,
    estimatedProfitRate: calcEstimatedProfitRate(totalPrice, totalCost)
  }
}
